<template>
  <BaseModal
    :heading="$t('register.salonized_pay.onboarding.heading', brandNamePay())"
    useForm
    @close="$emit('close')"
    @submit="
      () => {
        updateCompanyDetails();
        if ($gtm) {
          $gtm.trackEvent({ event: 'sp-company-details-save' });
        }
      }
    "
    testId="sp-onboarding"
  >
    <BaseSpinner v-if="loading && microSiteLoading" />
    <Flex
      direction="column"
      v-if="!loading && !microSiteLoading"
    >
      <Flex
        :direction="$screen === 's' ? 'column' : 'row'"
        equalWidth
      >
        <BaseInput
          v-model="companyDetails.name"
          required
          :label="$t('company.name')"
          v-test="'sp-onboarding-input-name'"
        />
        <BaseInput
          v-model="companyDetails.phone"
          required
          type="tel"
          :label="$t('company.phone_number')"
          :minLength="9"
          :maxLength="20"
          v-test="'sp-onboarding-input-phone'"
        />
      </Flex>
      <Flex
        :direction="$screen === 's' ? 'column' : 'row'"
        equalWidth
      >
        <BaseDropdown
          v-model="selectedLegalEntityType"
          required
          :label="$t('register.salonized_pay.new_onboarding.company_structure')"
          :options="adyenLegalEntityTypes"
          v-test="'sp-onboarding-input-selectedLegalEntityType'"
        />
        <BaseDropdown
          v-model="selectedIndustryCode"
          required
          :label="$t('register.salonized_pay.new_onboarding.company_type')"
          :options="aydenIndustryCodes"
          v-test="'sp-onboarding-input-selectedIndustryCode'"
        />
      </Flex>
      <Flex
        v-if="
          selectedLegalEntityType &&
          selectedLegalEntityType !== LegalEntity.Organization
        "
        :direction="$screen === 's' ? 'column' : 'row'"
        equalWidth
      >
        <BaseInput
          v-model="userDetails.firstName"
          :required="
            selectedLegalEntityType &&
            selectedLegalEntityType !== LegalEntity.Organization
          "
          :label="$t('register.salonized_pay.new_onboarding.owner_first_name')"
          v-test="'sp-onboarding-input-user-firstName'"
        />
        <BaseInput
          v-model="userDetails.lastName"
          :required="
            selectedLegalEntityType &&
            selectedLegalEntityType !== LegalEntity.Organization
          "
          :label="$t('register.salonized_pay.new_onboarding.owner_last_name')"
          v-test="'sp-onboarding-input-user-lastName'"
        />
      </Flex>

      <Flex
        :direction="$screen === 's' ? 'column' : 'row'"
        equalWidth
      >
        <BaseInput
          v-model="registrationNumber"
          :required="registrationNumberRequired"
          :label="
            formats[company.country as keyof typeof formats].registrationNumber
              .translation
          "
          :error="
            registrationNumberError
              ? formats[company.country as keyof typeof formats]
                  .registrationNumber.validation_error
              : null
          "
          :regex="
            formats[company.country as keyof typeof formats].registrationNumber
              .regex
          "
          :placeholder="
            formats[company.country as keyof typeof formats].registrationNumber
              .label
          "
          v-test="'sp-onboarding-input-registrationNumber'"
        />
        <Flex
          direction="column"
          :gap="0.5"
        >
          <SwissVatInput
            v-if="company.country === 'ch'"
            v-model="vatNumber"
            :companyLanguage="company.language"
            :required="!vatExemption"
            :disabled="vatExemption"
            :error="vatNumberError ? formats.ch.vat.validation_error : null"
          />
          <BaseInput
            v-else
            v-model="vatNumber"
            :required="!vatExemption"
            :disabled="vatExemption"
            :label="$t('register.salonized_pay.new_onboarding.vat_number')"
            :error="
              vatNumberError
                ? formats[company.country as keyof typeof formats].vat
                    .validation_error
                : null
            "
            :regex="
              vatExemption
                ? undefined
                : formats[company.country as keyof typeof formats].vat.regex
            "
            :placeholder="
              formats[company.country as keyof typeof formats].vat.label
            "
            v-test="'sp-onboarding-input-vatNumber'"
          />
          <BaseCheckbox
            v-model="vatExemption"
            :label="$t('register.salonized_pay.new_onboarding.vat_exemption')"
            v-test="'sp-onboarding-input-vatExemption'"
          />
        </Flex>
      </Flex>
      <BaseInput
        v-model="website"
        required
        :label="$t('register.salonized_pay.new_onboarding.website_url')"
        v-test="'sp-onboarding-input-website'"
      />
      <BaseInput
        v-model="companyDetails.address"
        required
        :label="$t('company.address')"
        v-test="'sp-onboarding-input-address'"
      />
      <Flex
        :direction="$screen === 's' ? 'column' : 'row'"
        equalWidth
      >
        <BaseInput
          v-model="companyDetails.postalcode"
          required
          type="postalcode"
          :label="$t('company.postal_code')"
          v-test="'sp-onboarding-input-postalcode'"
        />
        <BaseInput
          v-model="companyDetails.city"
          required
          :label="$t('company.city')"
          v-test="'sp-onboarding-input-city'"
        />
      </Flex>

      <BaseCheckbox
        v-if="
          selectedLegalEntityType &&
          selectedLegalEntityType === LegalEntity.SoleProprietorship
        "
        v-model="residentialAddressSame"
        :label="
          $t('register.salonized_pay.new_onboarding.residential_address_same')
        "
        v-test="'sp-onboarding-input-residentialAddressSame'"
      />

      <Flex
        direction="column"
        v-if="
          selectedLegalEntityType &&
          selectedLegalEntityType === LegalEntity.SoleProprietorship &&
          !residentialAddressSame
        "
      >
        <BaseInput
          v-model="residentialDetails.address"
          required
          :label="
            $t('register.salonized_pay.new_onboarding.residential_address')
          "
          v-test="'sp-onboarding-input-residentialAddress'"
        />
        <Flex
          :direction="$screen === 's' ? 'column' : 'row'"
          equalWidth
        >
          <BaseInput
            v-model="residentialDetails.postalcode"
            required
            type="postalcode"
            :label="$t('company.postal_code')"
            v-test="'sp-onboarding-input-residentialPostalcode'"
          />
          <BaseInput
            v-model="residentialDetails.city"
            required
            :label="$t('company.city')"
            v-test="'sp-onboarding-input-residentialCity'"
          />
        </Flex>
      </Flex>
    </Flex>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="$emit('close')"
      >
        {{ $t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="adyenOnboardingLoading"
        submitForm
        v-test="'sp-onboarding-save'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandNamePay } from '@/helpers/brand-name';
import { formatSwissRegistrationNumber } from '@/helpers/formatting';
import {
  BE_REGISTRATION_NUMBER_REGEX,
  BE_VAT_NUMBER_REGEX,
  CH_REGISTRATION_NUMBER_REGEX,
  CH_VAT_NUMBER_REGEX,
  DE_VAT_NUMBER_VALIDATION_REGEX,
  FR_SIRET_REGEX,
  FR_VAT_NUMBER_REGEX,
  HRB_NUMBER_VALIDATION_REGEX,
  KVK_NUMBER_VALIDATION_REGEX,
  NL_VAT_NUMBER_VALIDATION_REGEX,
  UK_REGISTRATION_NUMBER_REGEX,
  UK_VAT_NUMBER_REGEX,
} from '@/helpers/regex';
import { useCompanyStore } from '@/stores/company';
import { useMicroSiteStore } from '@/stores/micro-site';
import { useUserStore } from '@/stores/user';
import type { IndustryCode, OnboardingPrefill } from '@/types';
import { LegalEntity } from '@/types';
import { CREATE_ADYEN_ONBOARDING, GET_ADYEN_ONBOARDING_DATA } from './graphql';
import SwissVatInput from './SwissVatInput.vue';

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch']);

const ukFormat = {
  registrationNumber: {
    regex: UK_REGISTRATION_NUMBER_REGEX,
    label: '12345678',
    translation: t(
      'register.salonized_pay.new_onboarding.registration_number_be',
    ),
    validation_error: t(
      'register.salonized_pay.new_onboarding.registration_number_error',
    ),
  },
  vat: {
    regex: UK_VAT_NUMBER_REGEX,
    label: 'GB123456789',
    validation_error: t(
      'register.salonized_pay.new_onboarding.var_number_error_uk',
    ),
  },
};

const formats = {
  nl: {
    registrationNumber: {
      regex: KVK_NUMBER_VALIDATION_REGEX,
      label: '12345678',
      translation: t(
        'register.salonized_pay.new_onboarding.registration_number',
      ),
      validation_error: t(
        'register.salonized_pay.new_onboarding.registration_number_error',
      ),
    },
    vat: {
      regex: NL_VAT_NUMBER_VALIDATION_REGEX,
      label: 'NLXXXXXXXXXBXX',
      validation_error: t(
        'register.salonized_pay.new_onboarding.vat_number_error',
      ),
    },
  },
  de: {
    registrationNumber: {
      regex: HRB_NUMBER_VALIDATION_REGEX,
      label: 'HRB123456',
      translation: t(
        'register.salonized_pay.new_onboarding.registration_number_hrn',
      ),
      validation_error: t(
        'register.salonized_pay.new_onboarding.registration_number_error_hrn',
      ),
    },
    vat: {
      regex: DE_VAT_NUMBER_VALIDATION_REGEX,
      label: 'DE123456789',
      validation_error: t(
        'register.salonized_pay.new_onboarding.mwst_number_error',
      ),
    },
  },
  be: {
    registrationNumber: {
      regex: BE_REGISTRATION_NUMBER_REGEX,
      label: '1234567890',
      translation: t(
        'register.salonized_pay.new_onboarding.registration_number_be',
      ),
      validation_error: t(
        'register.salonized_pay.new_onboarding.registration_number_error_be',
      ),
    },
    vat: {
      regex: BE_VAT_NUMBER_REGEX,
      label: 'BE1234567890',
      validation_error: t(
        'register.salonized_pay.new_onboarding.vat_number_error_be',
      ),
    },
  },
  gb: ukFormat,
  je: ukFormat,
  gg: ukFormat,
  im: ukFormat,
  fr: {
    registrationNumber: {
      regex: FR_SIRET_REGEX,
      label: t('integrations.fiskaly_fr.siret_number_placeholder'),
      translation: t('integrations.fiskaly_fr.siret_number'),
      validation_error: t(
        'register.salonized_pay.new_onboarding.registration_number_error_fr',
      ),
    },
    vat: {
      regex: FR_VAT_NUMBER_REGEX,
      label: t('integrations.fiskaly_fr.vat_number_placeholder'),
      validation_error: t(
        'register.salonized_pay.new_onboarding.vat_number_error_fr',
      ),
    },
  },
  ch: {
    registrationNumber: {
      regex: CH_REGISTRATION_NUMBER_REGEX,
      label: 'CHE-123.456.789',
      translation: t(
        'register.salonized_pay.new_onboarding.registration_number_ch',
      ),
      validation_error: t(
        'register.salonized_pay.new_onboarding.registration_number_error_ch',
      ),
    },
    vat: {
      regex: CH_VAT_NUMBER_REGEX,
      label: 'CHE-123.456.789 MWST',
      validation_error: t(
        'register.salonized_pay.new_onboarding.vat_number_error_ch',
      ),
    },
  },
};

const { company } = useCompanyStore();
const { user } = useUserStore();
const { onResult: onAdyenDataResult, loading } = useQuery(
  GET_ADYEN_ONBOARDING_DATA,
);
const website = ref('');
const { microSite, loading: microSiteLoading } = storeToRefs(
  useMicroSiteStore(),
);
watch(
  microSite,
  () => {
    website.value = microSite.value?.customDomain || microSite.value?.url || '';
  },
  { immediate: true },
);

const { mutate: createAdyenOnboarding, loading: adyenOnboardingLoading } =
  useMutation(CREATE_ADYEN_ONBOARDING);

const adyenLegalEntityTypes = ref();
const selectedLegalEntityType = ref<LegalEntity>(
  LegalEntity.SoleProprietorship,
);
const aydenIndustryCodes = ref();
const selectedIndustryCode = ref();
const registrationNumber = ref('');
const vatNumber = ref('');
const vatExemption = ref(false);
const residentialAddressSame = ref(true);

watch(registrationNumber, (val) => {
  if (company.country === 'ch') {
    const formatted = formatSwissRegistrationNumber(val);
    if (formatted !== val) {
      registrationNumber.value = formatted;
    }
    return;
  }

  const upper = val.toUpperCase();
  if (val !== upper) {
    registrationNumber.value = upper;
  }
});

const registrationNumberRequired = computed(
  () =>
    !(
      selectedLegalEntityType.value === LegalEntity.SoleProprietorship &&
      ['de', 'ch'].includes(company.country)
    ),
);

const registrationNumberError = computed(() => {
  const registrationNumberInvalid = !registrationNumber.value.match(
    formats[company.country as keyof typeof formats].registrationNumber.regex,
  );
  return registrationNumber.value && registrationNumberInvalid;
});

const vatNumberError = computed(
  () =>
    !vatExemption.value &&
    vatNumber.value &&
    !vatNumber.value.match(
      formats[company.country as keyof typeof formats].vat.regex,
    ),
);

onAdyenDataResult(({ data }) => {
  aydenIndustryCodes.value = data.adyenOnboardingData.industryCodes.map(
    (code: IndustryCode) => ({
      label: t(
        `register.salonized_pay.onboarding.company_types.${code.name.toLowerCase()}`,
      ),
      value: code.id,
    }),
  );
  adyenLegalEntityTypes.value = data.adyenOnboardingData.legalEntityTypes.map(
    (type: LegalEntity) => ({
      label: t(
        `register.salonized_pay.new_onboarding.legal_entity.${type.toLowerCase()}`,
      ),
      value: type,
    }),
  );
  prefillForm(data.adyenOnboardingData.prefill);
});

const companyDetails = reactive({
  name: '',
  phone: '',
  address: '',
  postalcode: '',
  city: '',
});

const residentialDetails = reactive({
  address: '',
  postalcode: '',
  city: '',
});

const userDetails = reactive({
  firstName: '',
  lastName: '',
});

// Store defaults, overridden by the prefill where it has a value. VAT is the exception:
// a registration without a VAT number means franchise en base, so the exemption is
// ticked and the field cleared instead of falling back to the store.
const prefillForm = (prefill?: OnboardingPrefill | null) => {
  companyDetails.name = prefill?.name ?? company.name ?? '';
  companyDetails.phone = company.phone || '';
  companyDetails.address = prefill?.address ?? company.address ?? '';
  companyDetails.postalcode = prefill?.postalcode ?? company.postalcode ?? '';
  companyDetails.city = prefill?.city ?? company.city ?? '';
  userDetails.firstName = prefill?.owner?.firstName ?? user?.firstName ?? '';
  userDetails.lastName = prefill?.owner?.lastName ?? user?.lastName ?? '';
  selectedLegalEntityType.value =
    prefill?.legalEntityType ?? LegalEntity.SoleProprietorship;
  registrationNumber.value = prefill?.registrationNumber ?? '';
  vatNumber.value = prefill
    ? (prefill.vat.number ?? '')
    : company.billingInfo?.stripeTaxId?.value || '';
  vatExemption.value = prefill
    ? prefill.vat.exemption
    : company.billingInfo?.dontHaveVatNumber || false;
};

// Store defaults first, so the form is usable even if the onboarding query fails;
// the query result re-runs this with the prefill once it arrives.
prefillForm();

const updateCompanyDetails = () => {
  createAdyenOnboarding({
    input: {
      companyAttributes: companyDetails,
      residenceAttributes: residentialDetails,
      userAttributes: userDetails,
      microSiteUrl: website.value,
      industryCodeId: selectedIndustryCode.value,
      legalEntityType: selectedLegalEntityType.value,
      registrationNumber: registrationNumber.value,
      vatNumber: vatNumber.value,
      vatExemption: vatExemption.value,
      residentialAddressSame: residentialAddressSame.value,
    },
  }).then(() => {
    emit('close');
    emit('refetch');
  });
};
</script>
